import React from "react";
// Meter Reading Report page

// Default
import { useState, useEffect } from "react";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid2";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Box from "@mui/material/Box";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomOutlinedSelect from "../../forms/select/OutlinedCustomSelect";
import MonthlyUsageChart from "../charts/MonthlyUsageChart";
import HourlyUsageChart from "../charts/HourlyUsageChart";
import CustomDataTable from "../table/CustomTable";
import CustomSearch from "../search/Search";

// Utils
import { fetchGETRequest } from "../../utils/services";
import { formatDate } from "../../utils/helper";

// Svg

// Types
interface MeterReading {
  meterId: string;
  reading: string;
  timestamp: string;
  [key: string]: any;
}

interface SelectOption {
  value: string;
  label: string;
}

const MeterReadingReport: React.FC = () => {
  const [monthlyData, setMonthlyData] = useState<number[]>([]);
  const [hourlyData, setHourlyData] = useState<number[]>([]);
  const [meterReadings, setMeterReadings] = useState<MeterReading[]>([]);
  const [page, setPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [filteredMonthlyData, setFilteredMonthlyData] = useState<any[]>([]);
  const [filteredHourlyData, setFilteredHourlyData] = useState<any[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<SelectOption | null>(null);
  const [selectedHour, setSelectedHour] = useState<SelectOption | null>(null);
  const [searchId, setSearchId] = useState("");
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const downloadCSV = () => {
    const headers = Object.keys(meterReadings[0]).join(",");
    const rows = meterReadings
      .map((row) => Object.values(row).join(","))
      .join("\n");
    const csvContent = `${headers}\n${rows}`;

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${window.location.pathname.slice(1)}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const COLUMNS = ["Date", "Reading", "Status"];

  const monthlyUsageOptions = [
    { value: "3", label: "Last 3 Months" },
    { value: "6", label: "Last 6 Months" },
    { value: "9", label: "Last 9 Months" },
    { value: "12", label: "Last 12 Months" },
  ];

  const hourlyUsageOptions = [
    { value: "24", label: "24 Hours" },
    { value: "12", label: "12 Hours" },
    { value: "6", label: "6 Hours" },
  ];

  const months = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(i);
    return date.toLocaleString("en-US", { month: "short" });
  });

  const hours = Array.from({ length: 24 }, (_, i) => `${i}:00`);

  const handleFilterChange = (hours: any) => {
    const filteredData = hourlyData.slice(0, hours.value);
    setSelectedHour(hours);
    setFilteredHourlyData(filteredData);
  };

  useEffect(() => {
    setFilteredHourlyData(hourlyData);
  }, [hourlyData]);

  const handleMonthFilterChange = (months: any) => {
    setSelectedMonth(months);
    const filteredData = monthlyData.slice(-months.value);
    setFilteredMonthlyData(filteredData);
  };

  useEffect(() => {
    setFilteredMonthlyData(monthlyData.slice(-12));
  }, [monthlyData]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetchGETRequest(
          `plugins/telemetry/DEVICE/${searchId}/values/timeseries?keys=IP_KWH,EventCode,EventDescription&startTs=1730485800000&endTs=1736015340000&intervalType=WEEK&agg=MIN`,
          {},
          true
        );

        let monthlyConsumption: number[] = [];
        let hourlyConsumption: number[] = [];
        let readings: any[] = [];

        if (response?.data?.status !== 400) {
          const dataArray = Object.keys(response?.data).flatMap(
            (key) => (response.data as any)[key]
          );

          monthlyConsumption = Array(12).fill(0);
          hourlyConsumption = Array(24).fill(0);

          dataArray?.forEach((dataPoint) => {
            const date = formatDate(
              new Date(dataPoint?.ts),
              "DD/MM/YYYY HH:mm:ss"
            );
            const month = new Date(dataPoint.ts).getMonth();
            const hour = new Date(dataPoint.ts).getHours();
            const value = parseFloat(dataPoint?.value);

            monthlyConsumption[month] += value;
            hourlyConsumption[hour] += value;

            readings?.push({
              date,
              reading: `${value?.toFixed(2)} kWh`,
              status: "Actual",
            });
          });
        }

        setMonthlyData(monthlyConsumption);
        setHourlyData(hourlyConsumption);
        setMeterReadings(readings);
      } catch (error) {
        return error;
      }
    };

    fetchData();
  }, [searchId]);

  const rowsContent = meterReadings?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.date !== "Invalid date" ? item?.date : "-"}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.reading === "NaN kWh" ? "-" : item?.reading}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item?.status}
        </Typography>
      </TableCell>
    </TableRow>
  ));

  const arr = [
    {
      ts: "1738798094097",
      IP_KWH: "134.67",
      IP_MeterTime: "2025-02-05T11:30:08",
    },
    {
      ts: "23432424",
      IP_KWH: "1382",
      IP_MeterTime: "2025-02-asdsaT11:30:08",
    },
  ];

  return (
    <Layout>
      <Card
        className="meters_title_card"
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignContent: "center",
        }}
      >
        <Typography variant="h5">Meter Reading Report</Typography>

        <Stack direction={"row"}>
          <Box>
            <CustomSearch
              placeholder={"Search Meter Id"}
              sx={{ width: "400px" }}
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              className={"meter-command_report_top_card_search"}
            />
          </Box>
        </Stack>
      </Card>

      <Grid container spacing={2.25}>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Card sx={{ mt: "20px", maxHeight: "400px" }}>
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="h5">Monthly Usage</Typography>
                  <CustomOutlinedSelect
                    placeholder={"Select Month"}
                    options={monthlyUsageOptions}
                    width="fit-content"
                    onChange={(newValue) => handleMonthFilterChange(newValue)}
                    value={selectedMonth}
                  />
                </Stack>
              }
            />
            <Divider />

            {
              <CardContent>
                {filteredMonthlyData?.length === 0 && (
                  <Stack
                    direction={"row"}
                    justifyContent={"center"}
                    padding={"14px"}
                  >
                    <Typography variant="body1">No Data Found</Typography>
                  </Stack>
                )}
                {filteredMonthlyData?.length !== 0 && (
                  <MonthlyUsageChart
                    months={months}
                    filteredMonthlyData={filteredMonthlyData}
                  />
                )}
              </CardContent>
            }
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 6, lg: 6 }}>
          <Card sx={{ mt: "20px", maxHeight: "400px" }}>
            <CardHeader
              title={
                <Stack
                  direction={"row"}
                  justifyContent={"space-between"}
                  alignItems={"center"}
                >
                  <Typography variant="h5">Hourly Usage</Typography>
                  <CustomOutlinedSelect
                    placeholder={"Select Hours"}
                    options={hourlyUsageOptions}
                    width="fit-content"
                    onChange={(newValue) => handleFilterChange(newValue)}
                    value={selectedHour}
                  />
                </Stack>
              }
            />
            <Divider />

            <CardContent>
              {filteredHourlyData?.length === 0 && (
                <Stack
                  direction={"row"}
                  justifyContent={"center"}
                  padding={"14px"}
                >
                  <Typography variant="body1">No Data Found</Typography>
                </Stack>
              )}

              {filteredHourlyData?.length !== 0 && (
                <HourlyUsageChart
                  hours={hours}
                  filteredHourlyData={filteredHourlyData}
                />
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Report Details</Typography>
              <ContainedButton
                label={"Download CSV"}
                sx={{
                  width: "fit-content",
                  fontWeight: 600,
                  color: "#ffffff",
                }}
                onClick={() => downloadCSV()}
                disabled={meterReadings?.length === 0}
              />
            </Stack>
          }
        />
        <Divider />

        <CardContent sx={{ overflow: "auto", maxHeight: "600px" }}>
          <CustomDataTable
            columns={COLUMNS}
            pagination={true}
            rowsContent={rowsContent}
            Actions={true}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={meterReadings?.length}
          />
        </CardContent>
      </Card>
    </Layout>
  );
};

export default MeterReadingReport;
